import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { AreaChart, Area, ResponsiveContainer, Tooltip, YAxis } from "recharts";
import { Fire, TrendUp, Minus, Mountains, Snowflake, Sparkle } from "@phosphor-icons/react";
import { Users, MapPin, ExternalLink, Search } from "lucide-react";
import { countryName } from "../lib/countries";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 25;

const SELECT_CLS = "w-full sm:w-auto sm:min-w-[150px] sm:flex-1 px-3 py-2.5 rounded-xl border border-zinc-200 bg-white text-sm font-medium text-zinc-700 focus:outline-none focus:border-violet-400";

const STATUS_META = {
  exploding: { label: "Exploding", Icon: Fire, color: "#10b981", chip: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  rising: { label: "Rising", Icon: TrendUp, color: "#3b82f6", chip: "bg-blue-50 text-blue-700 ring-blue-200" },
  regular: { label: "Steady", Icon: Minus, color: "#71717a", chip: "bg-zinc-100 text-zinc-600 ring-zinc-200" },
  peaked: { label: "Peaked", Icon: Mountains, color: "#f59e0b", chip: "bg-amber-50 text-amber-700 ring-amber-200" },
  cooling: { label: "Cooling", Icon: Snowflake, color: "#ef4444", chip: "bg-red-50 text-red-600 ring-red-200" },
  collecting: { label: "Collecting…", Icon: Sparkle, color: "#a1a1aa", chip: "bg-zinc-100 text-zinc-500 ring-zinc-200" },
};

const SORTS = [
  { key: "growth", label: "Fastest growing" },
  { key: "volume", label: "Most searched" },
  { key: "founded", label: "Newest" },
];

const fmtGrowth = (g) => {
  if (g === null || g === undefined) return null;
  const a = Math.abs(g), sign = g < 0 ? "−" : "+";
  if (a >= 9900) return `${sign}99×+`;
  if (a >= 1000) return `${sign}${Math.round(a / 100)}×`;
  return `${sign}${Math.round(a)}%`;
};
const fmtVol = (v) => {
  if (v == null) return "—";
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return `${v}`;
};
const fmtFunding = (v) => {
  if (!v) return "—";
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${Math.round(v / 1e6)}M`;
  if (v >= 1e3) return `$${Math.round(v / 1e3)}K`;
  return `$${v}`;
};

const Sparkline = ({ data, color }) => {
  if (!data || data.length < 2) return <div className="h-12 flex items-center text-[10px] text-zinc-300">—</div>;
  const gid = `tsb-${color.replace("#", "")}`;
  return (
    <ResponsiveContainer width="100%" height={48}>
      <AreaChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.28} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <YAxis hide domain={[0, "dataMax"]} />
        <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e4e4e7", padding: "3px 7px" }}
          formatter={(v) => [`${fmtVol(v)} searches`, "monthly"]} />
        <Area type="monotone" dataKey="v" stroke={color} strokeWidth={2} fill={`url(#${gid})`} isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

const Row = ({ s }) => {
  const meta = STATUS_META[s.status] || STATUS_META.collecting;
  const growth = fmtGrowth(s.growth);
  const loc = [s.city, countryName(s.country)].filter(Boolean).join(", ");
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4 hover:border-violet-300 transition-colors" data-testid={`tsb-row-${s.slug}`}>
      <div className="flex items-start gap-4">
        {/* Company */}
        <div className="flex items-start gap-3 min-w-0 flex-1">
          {s.logo_url
            ? <img src={s.logo_url} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0 bg-zinc-100" onError={(e) => { e.target.style.display = "none"; }} />
            : <div className="w-9 h-9 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center font-bold flex-shrink-0">{(s.name || "?")[0]}</div>}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <a href={s.website || "#"} target="_blank" rel="noopener noreferrer" className="font-semibold text-zinc-900 hover:text-violet-700 truncate">{s.name}</a>
              {s.website && <ExternalLink size={12} className="text-zinc-300 flex-shrink-0" />}
            </div>
            <div className="text-xs text-zinc-500 line-clamp-2 mt-0.5">{s.description || "—"}</div>
            <div className="flex flex-wrap gap-1 mt-1.5">
              {(s.industries || []).slice(0, 3).map((c) => (
                <span key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500">{c}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Growth + sparkline */}
        <div className="hidden sm:block w-28 flex-shrink-0">
          <Sparkline data={s.series} color={meta.color} />
          <div className="text-center text-sm font-bold tabular-nums mt-0.5" style={{ color: meta.color }}>{growth || "—"}</div>
        </div>

        {/* Volume */}
        <div className="w-16 flex-shrink-0 text-right">
          <div className="text-sm font-bold text-zinc-900 tabular-nums">{fmtVol(s.avg_searches)}</div>
          <div className="text-[10px] text-zinc-400">searches/mo</div>
          <span className={`inline-flex sm:hidden items-center gap-1 mt-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ring-1 ${meta.chip}`}>{growth || meta.label}</span>
        </div>
      </div>

      {/* Firmographics row */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 pt-3 border-t border-zinc-100 text-[11px] text-zinc-500">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ring-1 ${meta.chip}`}>
          <meta.Icon size={11} weight="fill" /> {meta.label}
        </span>
        <span>Funding: <b className="text-zinc-700">{fmtFunding(s.funding_total_usd)}</b></span>
        {s.team_size && <span className="inline-flex items-center gap-1"><Users size={12} /> {s.team_size}</span>}
        {loc && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {loc}</span>}
        {s.founded_year && <span>Founded {s.founded_year}</span>}
        {s.acquisition_status === "acquired" && <span className="text-emerald-600 font-medium">Acquired</span>}
      </div>
    </div>
  );
};

export default function TrendingStartupsBoard() {
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState({ total: 0, with_volume: 0, categories: [], refreshed_at: null });
  const [sort, setSort] = useState("growth");
  const [category, setCategory] = useState("all");
  const [niche, setNiche] = useState("all");
  const [breakout, setBreakout] = useState(false);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/trending/startups-board/facets`, { params: niche !== "all" ? { niche } : {} })
      .then((r) => setFacets(r.data)).catch(() => {});
  }, [niche]);

  const fetchPage = useCallback((pageNum, replace) => {
    setLoading(true);
    axios.get(`${API}/trending/startups-board`, { params: { sort, category, niche, breakout, q, page: pageNum, page_size: PAGE_SIZE } })
      .then((r) => {
        setTotal(r.data.total);
        setItems((prev) => (replace ? r.data.items : [...prev, ...r.data.items]));
        setPage(pageNum);
      })
      .finally(() => setLoading(false));
  }, [sort, category, niche, breakout, q]);

  useEffect(() => {
    const t = setTimeout(() => fetchPage(1, true), 300);
    return () => clearTimeout(t);
  }, [sort, category, niche, breakout, q, fetchPage]);

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6" data-testid="trending-startups-board">
      <div className="text-xs font-bold tracking-widest text-violet-600 mb-2">TRENDING · STARTUPS</div>
      <h1 className="text-3xl sm:text-4xl font-extrabold text-zinc-900 mb-2">Startups people are searching for.</h1>
      <p className="text-zinc-500 max-w-3xl text-sm">
        {facets.with_volume?.toLocaleString()} startups tracked across our VC corpus + Exploding Topics, ranked by <b>real search-volume growth</b> on their brand name. Funding, team size & round are shown where we have them and "—" where we don't — never guessed.
      </p>
      {facets.refreshed_at && <p className="text-[11px] text-zinc-400 mt-1">Search volume refreshed {String(facets.refreshed_at).slice(0, 10)} · brand-name queries may include unrelated searches for common words.</p>}

      {/* Organized filter bar — search on top, dropdowns + breakout below.
          On mobile the dropdowns stack 2-up; Breakout spans full width. */}
      <div className="mt-6 mb-4 p-3 sm:p-4 rounded-2xl border border-zinc-200 bg-zinc-50/70">
        <div className="relative mb-2.5">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search startup or keyword…"
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-zinc-200 bg-white text-sm focus:outline-none focus:border-violet-400" data-testid="tsb-search" />
        </div>
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2">
          <select value={niche} onChange={(e) => { setNiche(e.target.value); setCategory("all"); }} data-testid="tsb-niche-select"
            className={SELECT_CLS}>
            <option value="all">All niches ({(facets.with_volume || 0).toLocaleString()})</option>
            {(facets.niches || []).map((n) => <option key={n.value} value={n.value}>{n.value} ({n.count})</option>)}
          </select>
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="tsb-cat-select"
            className={SELECT_CLS}>
            <option value="all">All categories</option>
            {(facets.categories || []).map((c) => <option key={c.value} value={c.value}>{c.value} ({c.count})</option>)}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} disabled={breakout} data-testid="tsb-sort"
            className={`${SELECT_CLS} ${breakout ? "opacity-40" : ""}`}>
            {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <button onClick={() => setBreakout((b) => !b)} data-testid="tsb-breakout-toggle"
            className={`col-span-2 sm:col-span-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-bold border transition-colors ${breakout ? "bg-orange-500 text-white border-orange-500" : "border-orange-200 text-orange-600 bg-orange-50 hover:bg-orange-100"}`}>
            <Fire size={16} weight="fill" /> Breakout
          </button>
        </div>
      </div>

      {breakout && (
        <div className="mb-4 p-3 rounded-xl bg-orange-50 border border-orange-200 flex items-start gap-2.5" data-testid="tsb-breakout-banner">
          <Fire size={18} weight="fill" className="text-orange-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-orange-900">
            <b>Breakout = recently funded × rising search.</b> Startups that took venture money <i>and</i> whose brand-name searches are accelerating — the earliest "about to pop" signal. Ranked by momentum × funding.
          </div>
        </div>
      )}


      <div className="text-sm text-zinc-500 mb-3">{total.toLocaleString()} startups</div>
      <div className="space-y-2">
        {items.map((s) => <Row key={s.slug} s={s} />)}
      </div>
      {loading && items.length === 0 && <div className="text-zinc-400 text-sm py-10">Loading…</div>}

      {!loading && items.length < total && (
        <div className="flex justify-center mt-6">
          <button onClick={() => fetchPage(page + 1, false)} data-testid="tsb-load-more"
            className="px-6 py-2.5 rounded-full border border-zinc-200 text-sm font-semibold text-zinc-700 hover:border-violet-300 transition-colors">
            Load more · {(total - items.length).toLocaleString()} more
          </button>
        </div>
      )}
    </div>
  );
}
